import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { errorText } from '../lib/api';
import { useRunScope } from '../lib/characters';
import { when } from '../lib/datetime';
import { MODIFIER_REGISTRY } from '../lib/effects/types';
import { stepText } from '../lib/effectsApi';
import {
  activeAbilities,
  explainSkill,
  playerSheets,
  type SheetEntryView,
  type SkillExplain,
} from '../lib/skillsApi';

const th = 'px-2 py-1 text-left';

const td = 'px-2 py-1';
const btn =
  'rounded border-2 border-ink px-2 py-0.5 text-xs font-semibold hover:bg-caption/40 disabled:opacity-40';
const input = 'rounded border-2 border-ink bg-white px-2 py-0.5 text-sm';

function SheetTable({ title, rows }: { title: string; rows: SheetEntryView[] }) {
  return (
    <>
      <h3 className="mt-2 text-sm font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm">None.</p>
      ) : (
        <table className="mt-1 w-full text-xs">
          <thead className="text-bruise">
            <tr>
              <th className={th}>Name</th>
              <th className={th}>Points</th>
              <th className={th}>Level</th>
              <th className={th}>Tenths</th>
              <th className={th}>Effective</th>
              <th className={th}>Peak</th>
              <th className={th}>Rusting</th>
              <th className={th}>Last used</th>
              <th className={th}>Mods</th>
              <th className={th}>Active abilities</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.kind}:${r.id}`} className="border-t border-newsprint align-top">
                <td className={td}>
                  <span className="font-semibold">{r.name || r.id}</span>{' '}
                  <span className="font-mono text-bruise">{r.id}</span>
                </td>
                <td className={`${td} font-mono`}>{r.points}</td>
                <td className={`${td} font-mono`}>{r.level}</td>
                <td className={`${td} font-mono`}>{Number(r.tenths ?? 0).toFixed(1)}</td>
                <td className={`${td} font-mono`}>{r.effective}</td>
                <td className={`${td} font-mono`}>{r.peak}</td>
                <td className={td}>
                  {r.rusting ? <span className="text-blam">rusting</span> : ''}
                </td>
                <td className={td}>
                  {r.lastUsedMs ? when(new Date(r.lastUsedMs).toISOString()) : '-'}
                </td>
                <td className={td}>
                  {(r.mods ?? [])
                    .map(
                      (m) =>
                        `${(m.delta ?? 0) > 0 ? '+' : ''}${m.delta ?? 0} ${m.name || m.effectId}`,
                    )
                    .join(', ')}
                </td>
                <td className={td} data-testid={`abilities-${r.kind}-${r.id}`}>
                  {activeAbilities(r) || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

/**
 * A player's Outta-Ten sheets (ADR 0066, CONTRACT §7): each crew member's stats and skills (points, level, tenths, effective, peak,
 * rusting, last used, live mods, the abilities active at 5 / 7 / 10, derived from the level: no picks since CONTRACT §13.4), and "Why is it this value?" through the skill explain (the evaluator's
 * trace for one member and key, like the Effects explain). Read-only; an older API's 404 shows an empty state.
 */
export function PlayerSkillsSection({ playerId }: { playerId: string }) {
  const { client, runId } = useRunScope();
  const q = useQuery({
    queryKey: ['admin-player-skills', playerId, runId],
    queryFn: () => playerSheets(client, playerId),
    retry: false,
  });
  const [member, setMember] = useState('player');
  const [key, setKey] = useState('combat.headshot');
  const [explain, setExplain] = useState<SkillExplain | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const run = useMutation({
    mutationFn: () => explainSkill(client, playerId, member, key),
    onSuccess: (x) => {
      setExplain(x);
      setProblem(null);
    },
    onError: (e) => setProblem(errorText(e)),
  });
  const members = q.data?.members ?? [];
  const shown = members.find((m) => m.memberId === member) ?? members[0];

  return (
    <section className="overflow-x-auto rounded border-2 border-ink bg-white p-4 lg:col-span-2">
      <h2 className="font-semibold">Stats and skills (Outta-Ten)</h2>
      {q.isPending && <p className="text-sm">Loading…</p>}
      {q.isError && <p className="text-sm text-blam">{errorText(q.error)}</p>}
      {q.data === null && (
        <p className="text-sm text-bruise" data-testid="sheets-empty">
          No sheets: this server has no Outta-Ten sheets.
        </p>
      )}
      {q.data && (
        <>
          {members.length === 0 && <p className="text-sm">No live run.</p>}
          {members.length > 0 && (
            <label className="text-xs font-semibold">
              Member{' '}
              <select
                aria-label="Sheet member"
                className={input}
                value={shown?.memberId ?? ''}
                onChange={(e) => setMember(e.target.value)}
              >
                {members.map((m) => (
                  <option key={m.memberId} value={m.memberId}>
                    {m.name || m.memberId} ({m.memberId})
                  </option>
                ))}
              </select>
            </label>
          )}
          {shown && (
            <>
              <SheetTable title="Stats" rows={shown.sheet.stats ?? []} />
              <SheetTable title="Skills" rows={shown.sheet.skills ?? []} />
            </>
          )}
        </>
      )}

      <h3 className="mt-3 text-sm font-semibold">Why is it this value?</h3>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs font-semibold">
          Member id
          <input
            aria-label="Explain member"
            className={`${input} block font-mono`}
            value={member}
            onChange={(e) => setMember(e.target.value)}
          />
        </label>
        <label className="text-xs font-semibold">
          Key
          <select
            aria-label="Explain key"
            className={`${input} block`}
            value={key}
            onChange={(e) => setKey(e.target.value)}
          >
            {MODIFIER_REGISTRY.map((k) => (
              <option key={k.key} value={k.key}>
                {k.key}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className={btn} onClick={() => run.mutate()} disabled={run.isPending}>
          Explain skill
        </button>
      </div>
      {explain && (
        <div
          className="mt-1 rounded border border-newsprint p-2 font-mono text-xs"
          data-testid="skill-explain"
        >
          <p>
            {explain.key} for {explain.target}: base {explain.base} → <b>{explain.result}</b>
          </p>
          <ol className="list-decimal pl-5">
            {(explain.steps ?? []).map((s, i) => (
              <li key={i}>{stepText(s)}</li>
            ))}
          </ol>
        </div>
      )}
      {problem && (
        <p className="mt-2 text-sm text-blam" role="alert">
          {problem}
        </p>
      )}
    </section>
  );
}
